<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ContactInfoRow from './ContactInfoRow.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ContactDeleteModal from 'dashboard/modules/contact/ContactDeleteModal.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';
import ContactAPI from 'dashboard/api/contacts';
import ScheduledMessageModal from 'dashboard/components/widgets/conversation/ScheduledMessageModal.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    EditContact,
    Avatar,
    ComposeConversation,
    SocialIcons,
    ContactMergeModal,
    ContactDeleteModal,
    VoiceCallButton,
    ScheduledMessageModal,
    InlineInput,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    channelType: {
      type: String,
      default: '',
    },
    conversationId: {
      type: [Number, String],
      default: null,
    },
    inboxId: {
      type: Number,
      default: null,
    },
  },
  emits: ['panelClose'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showEditModal: false,
      showDeleteModal: false,
      showScheduledMessageModal: false,
      isUpdatingAvatar: false,
      isSyncingMessages: false,
      isTogglingAgentName: false,
      isEditingName: false,
      editName: '',
      showAvatarPreview: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      getConversationById: 'getConversationById',
    }),
    currentConversation() {
      if (!this.conversationId) return null;
      return this.getConversationById(this.conversationId);
    },
    isSendAgentNameEnabled() {
      const conversation = this.currentConversation;
      if (!conversation || !conversation.custom_attributes) return true;
      const val = conversation.custom_attributes.send_agent_name;
      // Default to true if not set
      return val === undefined || val === null || val === true;
    },
    lastMessage() {
      if (!this.conversationId) return '';
      const conversation = this.getConversationById(this.conversationId);
      if (!conversation || !conversation.messages) return '';
      // Get the last non-private outgoing or incoming message
      const messages = [...conversation.messages].reverse();
      const lastMsg = messages.find(
        msg => !msg.private && msg.content && [0, 1].includes(msg.message_type)
      );
      return lastMsg?.content || '';
    },
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;

      const telegram = socialProfiles?.telegram || telegramUsername || '';
      const twitter = socialProfiles?.twitter || twitterScreenName || '';

      return {
        ...(socialProfiles || {}),
        twitter,
        telegram,
      };
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
  },
  methods: {
    dynamicTime,
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    startEditingName() {
      this.editName = this.contact.name || '';
      this.isEditingName = true;
      this.$nextTick(() => {
        this.$refs.nameInput?.focus();
      });
    },
    saveNameEdit() {
      if (!this.isEditingName) return;
      this.isEditingName = false;
      const trimmed = this.editName.trim();
      if (trimmed && trimmed !== this.contact.name) {
        this.updateContactField({ name: trimmed });
      }
    },
    cancelNameEdit() {
      this.isEditingName = false;
    },
    onFieldUpdate(field, value) {
      this.updateContactField({ [field]: value });
    },
    async updateContactField(attrs) {
      const contactId = this.contact.id;
      try {
        await this.$store.dispatch('contacts/update', {
          id: contactId,
          ...attrs,
        });
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
        await this.$store.dispatch('contacts/fetchContactableInbox', contactId);
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          const detail = error.contactErrorDetail;
          if (detail) {
            useAlert(detail);
          } else {
            const invalidAttrs = Array.isArray(error.data) ? error.data : [];
            if (invalidAttrs.includes('email')) {
              useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
            } else if (invalidAttrs.includes('phone_number')) {
              useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
            } else {
              useAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
            }
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(error.message || this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
    openScheduledMessageModal() {
      console.log('Opening scheduled message modal with:', {
        conversationId: this.conversationId,
        inboxId: this.inboxId,
        contactId: this.contact?.id,
      });
      this.showScheduledMessageModal = true;
    },
    closeScheduledMessageModal() {
      this.showScheduledMessageModal = false;
    },
    async updateAvatarFromWhatsapp() {
      if (!this.contact.phone_number) {
        useAlert(this.$t('CONTACT_PANEL.UPDATE_AVATAR.NO_PHONE'));
        return;
      }

      this.isUpdatingAvatar = true;
      try {
        await ContactAPI.updateAvatarFromWhatsapp(this.contact.id);
        useAlert(this.$t('CONTACT_PANEL.UPDATE_AVATAR.SUCCESS'));
        // Refresh contact data after a short delay to allow avatar to be processed
        setTimeout(() => {
          this.$store.dispatch('contacts/show', { id: this.contact.id });
        }, 2000);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error ||
          this.$t('CONTACT_PANEL.UPDATE_AVATAR.ERROR');
        useAlert(errorMessage);
      } finally {
        this.isUpdatingAvatar = false;
      }
    },
    async toggleSendAgentName() {
      this.isTogglingAgentName = true;
      try {
        const newValue = !this.isSendAgentNameEnabled;
        const conversation = this.currentConversation;
        const customAttributes = {
          ...(conversation?.custom_attributes || {}),
          send_agent_name: newValue,
        };
        await this.$store.dispatch('updateCustomAttributes', {
          conversationId: this.conversationId,
          customAttributes,
        });
        useAlert(
          newValue
            ? this.$t('CONTACT_PANEL.SEND_AGENT_NAME.ENABLED')
            : this.$t('CONTACT_PANEL.SEND_AGENT_NAME.DISABLED')
        );
      } catch (error) {
        useAlert(this.$t('CONTACT_PANEL.SEND_AGENT_NAME.ERROR'));
      } finally {
        this.isTogglingAgentName = false;
      }
    },
    async syncQuepasaMessages() {
      if (!this.contact.phone_number) {
        useAlert(this.$t('CONTACT_PANEL.SYNC_MESSAGES.NO_PHONE'));
        return;
      }

      this.isSyncingMessages = true;
      try {
        const response = await ContactAPI.syncQuepasaMessages(this.contact.id);
        const message =
          response.data?.message ||
          this.$t('CONTACT_PANEL.SYNC_MESSAGES.SUCCESS');
        useAlert(message);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error ||
          this.$t('CONTACT_PANEL.SYNC_MESSAGES.ERROR');
        useAlert(errorMessage);
      } finally {
        this.isSyncingMessages = false;
      }
    },
  },
};
</script>

<template>
  <div class="relative items-center w-full p-4">
    <div class="flex flex-col w-full gap-2 text-left rtl:text-right">
      <div class="flex flex-row justify-between">
        <Avatar
          v-if="showAvatar"
          :src="contact.thumbnail"
          :name="contact.name"
          :status="contact.availability_status"
          :size="48"
          hide-offline-status
          rounded-full
          class="cursor-pointer"
          @click="contact.thumbnail && (showAvatarPreview = true)"
        />
      </div>

      <div class="flex flex-col items-start gap-1.5 min-w-0 w-full">
        <div v-if="showAvatar" class="flex items-center w-full min-w-0 gap-3">
          <InlineInput
            v-if="isEditingName"
            ref="nameInput"
            v-model="editName"
            custom-input-class="!text-base !font-medium"
            class="!w-fit"
            @enter-press="saveNameEdit"
            @escape-press="cancelNameEdit"
            @blur="saveNameEdit"
          />
          <h3
            v-else
            class="group/name flex-shrink max-w-full min-w-0 my-0 text-base capitalize break-words text-n-slate-12 cursor-pointer hover:text-n-slate-12/80"
            :title="$t('CONTACT_PANEL.CLICK_TO_EDIT')"
            @click="startEditingName"
          >
            {{ contact.name }}
            <span
              class="i-lucide-pencil text-xs text-n-slate-10 opacity-0 group-hover/name:opacity-100 transition-opacity ml-1 align-middle"
            />
          </h3>
          <div class="flex flex-row items-center gap-2">
            <span
              v-if="contact.created_at"
              v-tooltip.left="
                `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                  contact.created_at
                )}`
              "
              class="i-lucide-info text-sm text-n-slate-10"
            />
            <a
              :href="contactProfileLink"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="leading-3"
            >
              <span class="i-lucide-external-link text-sm text-n-slate-10" />
            </a>
          </div>
        </div>

        <p v-if="additionalAttributes.description" class="break-words mb-0.5">
          {{ additionalAttributes.description }}
        </p>
        <div class="flex flex-col items-start w-full gap-2">
          <ContactInfoRow
            :href="contact.email ? `mailto:${contact.email}` : ''"
            :value="contact.email"
            icon="mail"
            emoji="✉️"
            :title="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
            show-copy
            editable
            @update="value => onFieldUpdate('email', value)"
          />
          <ContactInfoRow
            :href="contact.phone_number ? `tel:${contact.phone_number}` : ''"
            :value="contact.phone_number"
            icon="call"
            emoji="📞"
            :title="$t('CONTACT_PANEL.PHONE_NUMBER')"
            show-copy
            editable
            @update="value => onFieldUpdate('phone_number', value)"
          />
          <ContactInfoRow
            v-if="contact.identifier"
            :value="contact.identifier"
            icon="contact-identify"
            emoji="🪪"
            :title="$t('CONTACT_PANEL.IDENTIFIER')"
          />
          <ContactInfoRow
            :value="additionalAttributes.company_name"
            icon="building-bank"
            emoji="🏢"
            :title="$t('CONTACT_PANEL.COMPANY')"
            editable
            @update="
              value =>
                updateContactField({
                  additional_attributes: {
                    ...additionalAttributes,
                    company_name: value,
                  },
                })
            "
          />
          <ContactInfoRow
            v-if="location || additionalAttributes.location"
            :value="location || additionalAttributes.location"
            icon="map"
            emoji="🌍"
            :title="$t('CONTACT_PANEL.LOCATION')"
          />
          <SocialIcons :social-profiles="socialProfiles" />

          <!-- UTM / Origem do Trafego -->
          <div
            v-if="contact.utm_source || contact.utm_medium"
            class="flex flex-col gap-1.5 w-full mt-1 p-2.5 rounded-lg bg-n-alpha-1 border border-n-weak"
          >
            <div class="flex items-center gap-1.5 mb-0.5">
              <span class="i-lucide-radar size-3.5 text-n-brand" />
              <span class="text-xs font-medium text-n-slate-11">Origem do Trafego</span>
            </div>
            <div class="flex flex-wrap gap-1.5">
              <span
                v-if="contact.utm_source"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-n-blue-3 text-n-blue-11"
              >
                {{ contact.utm_source }}
              </span>
              <span
                v-if="contact.utm_medium"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-n-violet-3 text-n-violet-11"
              >
                {{ contact.utm_medium }}
              </span>
              <span
                v-if="contact.utm_campaign"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-n-green-3 text-n-green-11"
                :title="contact.utm_campaign"
              >
                {{ contact.utm_campaign.length > 30 ? contact.utm_campaign.substring(0, 30) + '...' : contact.utm_campaign }}
              </span>
              <span
                v-if="additionalAttributes.status_cliente"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-n-amber-3 text-n-amber-11"
              >
                {{ additionalAttributes.status_cliente }}
              </span>
              <span
                v-if="additionalAttributes.resultado_lead"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-n-red-3 text-n-red-11"
              >
                {{ additionalAttributes.resultado_lead }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="flex items-center w-full mt-0.5 gap-2">
        <ComposeConversation :contact-id="String(contact.id)">
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
              icon="i-ph-chat-circle-dots"
              slate
              faded
              sm
            />
          </template>
        </ComposeConversation>
        <VoiceCallButton
          :phone="contact.phone_number"
          :contact-id="contact.id"
          :conversation-id="conversationId"
          icon="i-ri-phone-fill"
          size="sm"
          :tooltip-label="$t('CONTACT_PANEL.CALL')"
          slate
          faded
        />
        <NextButton
          v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
          icon="i-ph-pencil-simple"
          slate
          faded
          sm
          @click="toggleEditModal"
        />
        <ContactMergeModal :primary-contact="contact">
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
              icon="i-ph-arrows-merge"
              slate
              faded
              sm
              :disabled="uiFlags.isMerging"
            />
          </template>
        </ContactMergeModal>
        <ContactDeleteModal
          v-if="isAdmin"
          :contact="contact"
          @deleted="$emit('panelClose')"
        >
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
              icon="i-ph-trash"
              slate
              faded
              sm
              ruby
              :disabled="uiFlags.isDeleting"
            />
          </template>
        </ContactDeleteModal>
        <NextButton
          v-if="contact.phone_number"
          v-tooltip.top-end="$t('CONTACT_PANEL.UPDATE_AVATAR.BUTTON')"
          icon="i-ph-camera"
          slate
          faded
          sm
          :disabled="isUpdatingAvatar"
          :is-loading="isUpdatingAvatar"
          @click="updateAvatarFromWhatsapp"
        />
        <NextButton
          v-if="contact.phone_number"
          v-tooltip.top-end="$t('CONTACT_PANEL.SYNC_MESSAGES.BUTTON')"
          icon="i-lucide-message-square-plus"
          slate
          faded
          sm
          :disabled="isSyncingMessages"
          :is-loading="isSyncingMessages"
          @click="syncQuepasaMessages"
        />
        <NextButton
          v-tooltip.top-end="$t('SCHEDULED_MESSAGES.BUTTON_TOOLTIP')"
          icon="i-lucide-clock"
          slate
          faded
          sm
          @click="openScheduledMessageModal"
        />
        <NextButton
          v-if="conversationId"
          v-tooltip.top-end="
            isSendAgentNameEnabled
              ? $t('CONTACT_PANEL.SEND_AGENT_NAME.DISABLE')
              : $t('CONTACT_PANEL.SEND_AGENT_NAME.ENABLE')
          "
          :icon="
            isSendAgentNameEnabled
              ? 'i-lucide-user-check'
              : 'i-lucide-user-x'
          "
          slate
          faded
          sm
          :class="{ 'opacity-50': !isSendAgentNameEnabled }"
          :disabled="isTogglingAgentName"
          :is-loading="isTogglingAgentName"
          @click="toggleSendAgentName"
        />
      </div>
      <EditContact
        :show="showEditModal"
        :contact="contact"
        @cancel="toggleEditModal"
      />
      <ScheduledMessageModal
        v-if="showScheduledMessageModal"
        :show="showScheduledMessageModal"
        :contact="contact"
        :conversation-id="conversationId"
        :inbox-id="inboxId"
        :initial-message="lastMessage"
        @close="closeScheduledMessageModal"
      />
    </div>
    <Teleport to="body">
      <div
        v-if="showAvatarPreview"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 cursor-pointer"
        @click="showAvatarPreview = false"
      >
        <img
          :src="contact.thumbnail"
          :alt="contact.name"
          class="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl object-contain"
          @click.stop
        />
      </div>
    </Teleport>
  </div>
</template>
